import React, { useCallback, useEffect, useState } from 'react';
import { Play, Phone, Bot, Clock } from 'lucide-react';
import { api } from '../services/api';
import { theme } from '../theme';
import { StatCard } from '../components/StatCard';
import { LoadingSpinner } from '../components/LoadingSpinner';

type Call = {
  id: string;
  lead_name: string | null;
  phone_number: string | null;
  duration_seconds: number;
  status: string | null;
  outcome: string | null;
  recording_url: string | null;
  called_at: string;
};

function callStatusBadgeClass(s: string | null): string {
  const x = (s || '').toLowerCase();
  if (x === 'completed') return 'odc-badge-green';
  if (x === 'busy') return 'odc-badge-amber';
  if (x === 'no_answer') return 'odc-badge-cyan';
  if (x === 'failed') return 'odc-badge-red';
  return 'odc-badge-accent';
}

function callStatusLabel(s: string | null): string {
  const x = (s || '').toLowerCase();
  if (x === 'completed') return 'Completed';
  if (x === 'busy') return 'Busy';
  if (x === 'no_answer') return 'No answer';
  if (x === 'failed') return 'Failed';
  return s || '—';
}

function formatDur(sec: number): string {
  if (!sec) return '—';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function formatHour12(h: number): string {
  if (h === 0) return '12 AM';
  if (h < 12) return `${h} AM`;
  if (h === 12) return '12 PM';
  return `${h - 12} PM`;
}

export function CallsPage() {
  const [calls, setCalls] = useState<Call[]>([]);
  const [stats, setStats] = useState({
    total: 0,
    answered: 0,
    appointments: 0,
    avg_duration_seconds: 0,
  });
  const [statusF, setStatusF] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const params: Record<string, string> = {};
    if (statusF) params.status = statusF;
    const [c, st] = await Promise.all([
      api.get<{ calls: Call[] }>('/calls', { params }),
      api.get<{ stats: typeof stats }>('/calls/stats'),
    ]);
    setCalls(c.data.calls);
    setStats(st.data.stats);
    setLoading(false);
  }, [statusF]);

  useEffect(() => {
    setLoading(true);
    void load().catch(() => setLoading(false));
  }, [load]);

  if (loading) return <LoadingSpinner />;

  const hours = Array.from({ length: 10 }, (_, i) => i + 9);

  return (
    <div className="odc-page relative z-10 space-y-8">
      <header>
        <div className="inline-flex items-center gap-2 rounded-full border px-3 py-1" style={{ borderColor: 'var(--border2)', background: 'var(--card)' }}>
          <Phone className="h-3.5 w-3.5" style={{ color: theme.success }} />
          <span className="text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color: theme.success }}>
            Voice AI
          </span>
        </div>
        <h1 className="mt-3 text-2xl font-bold md:text-3xl" style={{ color: 'var(--text)' }}>
          AI calls
        </h1>
        <p className="mt-1 max-w-lg text-sm" style={{ color: 'var(--muted)' }}>
          Dialer window, call logs, and outcomes — same glass aesthetic as the main dashboard.
        </p>
      </header>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard title="Total calls" value={stats.total} stagger={1} />
        <StatCard title="Answered" value={stats.answered} accent={theme.success} stagger={2} />
        <StatCard title="Appointments" value={stats.appointments} accent={theme.accent} stagger={3} />
        <StatCard
          title="Avg duration"
          value={formatDur(stats.avg_duration_seconds)}
          accent={theme.secondary}
          stagger={4}
        />
      </div>

      <div
        className="odc-card relative overflow-hidden !p-5 sm:!p-6"
        style={{
          transform: 'none',
          borderColor: 'rgba(16,185,129,0.38)',
          background: `linear-gradient(135deg, var(--card) 0%, var(--bg2) 100%)`,
        }}
      >
        <div
          className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full opacity-40"
          style={{ background: `radial-gradient(circle, ${theme.primary}55, transparent 70%)` }}
        />
        <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-4">
            <div
              className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl text-2xl text-white"
              style={{
                background: `linear-gradient(135deg, ${theme.primary}, ${theme.secondary})`,
                boxShadow: '0 8px 28px rgba(16,185,129,0.35)',
              }}
            >
              <Bot className="h-7 w-7" />
            </div>
            <div>
              <h3 className="flex items-center gap-2 text-lg font-bold" style={{ color: 'var(--text)' }}>
                AI dialer
                <span className="odc-badge odc-badge-green text-[9px]">Live window</span>
              </h3>
              <p className="mt-1 flex items-center gap-2 text-sm" style={{ color: 'var(--muted)' }}>
                <Clock className="h-4 w-4 shrink-0" />
                Outbound calls run between 9:00 AM and 6:00 PM in your timezone.
              </p>
            </div>
          </div>
          <button type="button" className="odc-btn-primary shrink-0" onClick={() => alert('Dialer queue — connect your telephony provider to go live.')}>
            <Phone className="h-4 w-4" />
            Start campaign
          </button>
        </div>
        <div className="relative mt-6 border-t pt-5" style={{ borderColor: 'var(--border)' }}>
          <p className="mb-3 text-[10px] font-bold uppercase tracking-[0.15em]" style={{ color: 'var(--muted)' }}>
            Calling hours
          </p>
          <div className="flex flex-wrap gap-2">
            {hours.map((h) => {
              const inWindow = h >= 9 && h <= 18;
              return (
                <div
                  key={h}
                  className={`odc-pill-tab !cursor-default text-xs ${inWindow ? 'active' : ''}`}
                  style={{
                    borderColor: inWindow ? theme.primary : 'var(--border)',
                    background: inWindow ? `${theme.primary}22` : 'var(--card)',
                    color: inWindow ? 'var(--text)' : 'var(--muted)',
                  }}
                >
                  {formatHour12(h)}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <span className="text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--muted)' }}>
          Filter
        </span>
        <select
          className="odc-select w-auto !py-2 text-sm"
          value={statusF}
          onChange={(e) => setStatusF(e.target.value)}
        >
          <option value="">All statuses</option>
          <option value="completed">Completed</option>
          <option value="no_answer">No answer</option>
          <option value="busy">Busy</option>
          <option value="failed">Failed</option>
        </select>
      </div>

      <div className="odc-card overflow-x-auto !rounded-2xl !p-0" style={{ transform: 'none' }}>
        <table className="odc-table min-w-[900px] text-sm">
          <thead>
            <tr>
              <th>Lead</th>
              <th>Number</th>
              <th>Duration</th>
              <th>Status</th>
              <th>Outcome</th>
              <th>Time</th>
              <th>Rec</th>
            </tr>
          </thead>
          <tbody>
            {calls.length === 0 ? (
              <tr>
                <td colSpan={7} className="!py-12 text-center" style={{ color: 'var(--muted)' }}>
                  No calls logged yet.
                </td>
              </tr>
            ) : (
              calls.map((c) => (
                <tr key={c.id}>
                  <td className="font-medium" style={{ color: 'var(--text)' }}>
                    {c.lead_name || '—'}
                  </td>
                  <td className="font-mono text-xs" style={{ color: 'var(--muted)' }}>
                    {c.phone_number || '—'}
                  </td>
                  <td style={{ color: 'var(--text)' }}>{formatDur(c.duration_seconds)}</td>
                  <td>
                    <span className={`odc-badge ${callStatusBadgeClass(c.status)} text-[10px]`}>{callStatusLabel(c.status)}</span>
                  </td>
                  <td className="capitalize" style={{ color: 'var(--muted)' }}>
                    {(c.outcome || '—').replace(/_/g, ' ')}
                  </td>
                  <td className="text-xs" style={{ color: 'var(--muted)' }}>
                    {new Date(c.called_at).toLocaleString()}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="odc-btn-ghost !p-2"
                      style={{ color: c.recording_url ? theme.primary : 'var(--muted)' }}
                      disabled={!c.recording_url}
                      title={c.recording_url ? 'Play recording' : 'No recording'}
                      onClick={() => c.recording_url && window.open(c.recording_url, '_blank')}
                    >
                      <Play className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
